"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createResourceHook = void 0;
var use_resolved_state_1 = require("@clover-util/use-resolved-state");
/**
 * "High order hook" (HOH) - A hook returning function
 *
 * Allows for the composition of a useResource hook based on a useRequest hook
 *
 * A "Resource" is defined as data that is returned by a API call.
 *
 * Typically rendering a component with data from an API call
 * we must retrieve a resource when the component mounts.
 *
 * This HOH provides a standardized wrapper for such behavior.
 *
 * To use this HOH, first define a custom RequestHook, then declare your custom hook:
 *
 * const useCustomResource = requestHook(useCustomRequest);
 *
 * @param useHook {RequestHook} hook which performs the request.
 */
function createResourceHook(useHook) {
    /**
     * Returns a flag, value, and error for the resource described by the factory
     *
     * Hook will cancel pending requests and re-fetch when dependencies change.
     *
     * Note: if request returns undefined, the old value will not be cleared.
     *
     * @example
     * const [ hasPending, list, error ] = useRequest<List>(() => {
     *   // return falsy to prevent the request
     *   if (page == 0) return;
     *
     *   return {
     *     url: `${id}/items`,
     *     params: { page }
     *   };
     * }, [page]);
     *
     * @param factory {ConfigFactory} - function that can return falsy or an AxiosRequestConfig object; returning falsy prevents the request from going out
     * @param deps {DependencyList} - react dependency list
     */
    return function useResource(factory, deps) {
        var _a = useHook(factory, deps), hasPending = _a[0], request = _a[1], cancel = _a[2];
        var _b = (0, use_resolved_state_1.useResolvedState)(request, cancel), value = _b[0], error = _b[1];
        return Object.assign([hasPending, value, error], { hasPending: hasPending, value: value, error: error });
    };
}
exports.createResourceHook = createResourceHook;
